/**
 * @Module: useRecipes Hook
 * @Status: Ready
 * @Summary: React hook for recipe CRUD — Firestore with localStorage fallback
 * @Description: Lazy-inits the Firestore recipe service when the user is signed in
 *   AND Firebase is configured. When Firestore is unavailable (API not enabled),
 *   falls back to localStorage and surfaces a toast so the user knows.
 *   Re-fetches the recipe list whenever the user identity changes.
 * @NextSteps: Add optimistic updates, real-time listener via onSnapshot
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { isFirebaseConfigured, db } from '../config/firebase';
import { createRecipeService } from '../services/firestore/recipe.service';
import type { StoredRecipe } from '../types/stored-recipe';
import { CURRENT_SCHEMA_VERSION } from '../types/stored-recipe';
import type { Recipe } from '../types/recipe';
import { AppError } from '../services/firestore/errors';

const LOCAL_STORAGE_KEY = 'choppy:recipes';

/** Read recipes array from localStorage. Returns [] on any failure. */
function loadLocalRecipes(): StoredRecipe[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredRecipe[]) : [];
  } catch {
    return [];
  }
}

/** Persist recipes array to localStorage. Silently swallows errors. */
function saveLocalRecipes(recipes: StoredRecipe[]): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(recipes));
  } catch { /* quota exceeded — nothing we can do */ }
}

/** Build a StoredRecipe shell for localStorage saves. */
function toStoredRecipe(recipe: Recipe): StoredRecipe {
  const now = new Date().toISOString();
  return {
    ...recipe,
    id: recipe.id || crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    isFavorite: false,
    cookCount: 0,
    lastCookedAt: null,
    userTags: [],
    schemaVersion: CURRENT_SCHEMA_VERSION,
  };
}

interface UseRecipesReturn {
  recipes: StoredRecipe[];
  loading: boolean;
  error: AppError | null;
  save: (recipe: Recipe) => Promise<StoredRecipe | null>;
  update: (recipeId: string, recipe: Recipe) => Promise<void>;
  remove: (recipeId: string) => Promise<void>;
  toggleFavorite: (recipeId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useRecipes(): UseRecipesReturn {
  const { user } = useAuth();
  const toast = useToast();
  const [recipes, setRecipes] = useState<StoredRecipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  /** True after Firestore has proven unavailable this session. */
  const firestoreDown = useRef(false);

  // Lazy-init: only create service when db is non-null
  const serviceRef = useRef(
    isFirebaseConfigured() && db ? createRecipeService(db) : null,
  );

  const uid = user?.uid ?? null;

  /** Show a one-time toast when falling back to localStorage. */
  const notifyLocalFallback = useCallback(() => {
    if (!firestoreDown.current) {
      firestoreDown.current = true;
      toast({
        title: 'Saved locally',
        description:
          'Enable Firestore in the Firebase console for cloud sync.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
    }
  }, [toast]);

  /** True when the error signals Firestore is not provisioned. */
  const isServiceUnavailable = (err: unknown): boolean =>
    err instanceof AppError && err.code === 'SERVICE_UNAVAILABLE';

  const fetchRecipes = useCallback(async () => {
    if (!uid) {
      setRecipes([]);
      return;
    }

    if (firestoreDown.current || !serviceRef.current) {
      setRecipes(loadLocalRecipes());
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const list = await serviceRef.current.list(uid);
      setRecipes(list);
    } catch (err) {
      if (isServiceUnavailable(err)) {
        notifyLocalFallback();
        setRecipes(loadLocalRecipes());
      } else {
        setError(err as AppError);
      }
    } finally {
      setLoading(false);
    }
  }, [uid, notifyLocalFallback]);

  // Re-fetch when user changes
  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const save = useCallback(
    async (recipe: Recipe): Promise<StoredRecipe | null> => {
      if (!uid) return null;
      setError(null);

      // localStorage fallback path
      if (firestoreDown.current || !serviceRef.current) {
        const stored = toStoredRecipe(recipe);
        setRecipes((prev) => {
          const next = [stored, ...prev];
          saveLocalRecipes(next);
          return next;
        });
        notifyLocalFallback();
        return stored;
      }

      try {
        const stored = await serviceRef.current.create(uid, recipe);
        setRecipes((prev) => [stored, ...prev]);
        return stored;
      } catch (err) {
        if (isServiceUnavailable(err)) {
          notifyLocalFallback();
          const stored = toStoredRecipe(recipe);
          setRecipes((prev) => {
            const next = [stored, ...prev];
            saveLocalRecipes(next);
            return next;
          });
          return stored;
        }
        setError(err as AppError);
        throw err;
      }
    },
    [uid, notifyLocalFallback],
  );

  const update = useCallback(
    async (recipeId: string, recipe: Recipe): Promise<void> => {
      if (!uid) return;
      setError(null);

      // localStorage fallback path
      if (firestoreDown.current || !serviceRef.current) {
        setRecipes((prev) => {
          const next = prev.map((r) =>
            r.id === recipeId
              ? { ...r, ...recipe, id: recipeId, updatedAt: new Date().toISOString() }
              : r,
          );
          saveLocalRecipes(next);
          return next;
        });
        notifyLocalFallback();
        return;
      }

      try {
        await serviceRef.current.update(uid, recipeId, recipe);
        setRecipes((prev) =>
          prev.map((r) =>
            r.id === recipeId
              ? { ...r, ...recipe, id: recipeId, updatedAt: new Date().toISOString() }
              : r,
          ),
        );
      } catch (err) {
        if (isServiceUnavailable(err)) {
          notifyLocalFallback();
          setRecipes((prev) => {
            const next = prev.map((r) =>
              r.id === recipeId
                ? { ...r, ...recipe, id: recipeId, updatedAt: new Date().toISOString() }
                : r,
            );
            saveLocalRecipes(next);
            return next;
          });
        } else {
          setError(err as AppError);
          throw err;
        }
      }
    },
    [uid, notifyLocalFallback],
  );

  const remove = useCallback(
    async (recipeId: string): Promise<void> => {
      if (!uid) return;
      setError(null);

      if (firestoreDown.current || !serviceRef.current) {
        setRecipes((prev) => {
          const next = prev.filter((r) => r.id !== recipeId);
          saveLocalRecipes(next);
          return next;
        });
        return;
      }

      try {
        await serviceRef.current.delete(uid, recipeId);
        setRecipes((prev) => prev.filter((r) => r.id !== recipeId));
      } catch (err) {
        if (isServiceUnavailable(err)) {
          notifyLocalFallback();
          setRecipes((prev) => {
            const next = prev.filter((r) => r.id !== recipeId);
            saveLocalRecipes(next);
            return next;
          });
        } else {
          setError(err as AppError);
          throw err;
        }
      }
    },
    [uid, notifyLocalFallback],
  );

  const toggleFavorite = useCallback(
    async (recipeId: string): Promise<void> => {
      if (!uid) return;
      setError(null);

      if (firestoreDown.current || !serviceRef.current) {
        setRecipes((prev) => {
          const next = prev.map((r) =>
            r.id === recipeId ? { ...r, isFavorite: !r.isFavorite } : r,
          );
          saveLocalRecipes(next);
          return next;
        });
        return;
      }

      try {
        const newVal = await serviceRef.current.toggleFavorite(uid, recipeId);
        setRecipes((prev) =>
          prev.map((r) =>
            r.id === recipeId ? { ...r, isFavorite: newVal } : r,
          ),
        );
      } catch (err) {
        if (isServiceUnavailable(err)) {
          notifyLocalFallback();
          setRecipes((prev) => {
            const next = prev.map((r) =>
              r.id === recipeId ? { ...r, isFavorite: !r.isFavorite } : r,
            );
            saveLocalRecipes(next);
            return next;
          });
        } else {
          setError(err as AppError);
          throw err;
        }
      }
    },
    [uid, notifyLocalFallback],
  );

  return {
    recipes,
    loading,
    error,
    save,
    update,
    remove,
    toggleFavorite,
    refresh: fetchRecipes,
  };
}
